import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getAgoraEngine } from '../rtc/agoraEngine'
import { ConnectionStatus, NetworkQualityInfo, RemoteUserState } from '../types'

const STABILITY_WINDOW = 10
const DEGRADE_RATIO = 0.7
const RECOVER_RATIO = 0.8
const MIN_HOLD_MS = 5_000

const STATUS_RANK: Record<ConnectionStatus, number> = {
  excellent: 0,
  good: 1,
  poor: 2,
  critical: 3,
  disconnected: 4,
}

function toConnectionStatus(overallQuality: number): ConnectionStatus {
  if (overallQuality <= 1) return 'excellent'
  if (overallQuality <= 2) return 'good'
  if (overallQuality <= 4) return 'poor'
  if (overallQuality <= 6) return 'critical'
  return 'disconnected'
}

function toConnectionMessage(status: ConnectionStatus) {
  switch (status) {
    case 'excellent':
      return 'Sua conexão está excelente.'
    case 'good':
      return 'Sua conexão está estável.'
    case 'poor':
      return 'Sua conexão está instável. Reduza uso de rede.'
    case 'critical':
      return 'Sua conexão está crítica. Video e audio podem falhar.'
    default:
      return 'Desconectado da chamada.'
  }
}

function resolveStableStatus(
  window: ConnectionStatus[],
  currentDisplayed: ConnectionStatus
): ConnectionStatus | null {
  if (window.length < 3) return null

  const counts: Record<ConnectionStatus, number> = {
    excellent: 0,
    good: 0,
    poor: 0,
    critical: 0,
    disconnected: 0,
  }
  for (const s of window) {
    counts[s]++
  }

  let dominant: ConnectionStatus = currentDisplayed
  let maxCount = 0
  for (const [status, count] of Object.entries(counts) as [
    ConnectionStatus,
    number,
  ][]) {
    if (count > maxCount) {
      maxCount = count
      dominant = status
    }
  }

  if (dominant === currentDisplayed) return null

  const isDegrading = STATUS_RANK[dominant] > STATUS_RANK[currentDisplayed]
  const ratio = isDegrading ? DEGRADE_RATIO : RECOVER_RATIO
  const required = Math.ceil(window.length * ratio)

  console.info('dominant', dominant)
  console.info('required', required)
  console.info('maxCount', maxCount)

  if (maxCount >= required) return dominant
  return null
}

export function useAgoraRoom() {
  const engine = useMemo(() => getAgoraEngine(), [])
  const mountedRef = useRef(true)

  const [remoteUsers, setRemoteUsers] = useState<RemoteUserState[]>([])
  const [connectionState, setConnectionState] = useState('DISCONNECTED')
  const [networkQuality, setNetworkQuality] = useState<NetworkQualityInfo>({
    txQuality: 0,
    rxQuality: 0,
    overallQuality: 0,
    timestamp: Date.now(),
  })
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>('disconnected')
  const [networkMessage, setNetworkMessage] = useState(
    'Desconectado da chamada.'
  )
  const [joinError, setJoinError] = useState<string | null>(null)
  const [tokenWillExpire, setTokenWillExpire] = useState(false)
  const [tokenDidExpire, setTokenDidExpire] = useState(false)
  const [isJoining, setIsJoining] = useState(false)
  // Um join cancelado pode terminar depois do seguinte começar; só o último
  // join zera isJoining.
  const joinRequestRef = useRef(0)

  const statusWindowRef = useRef<ConnectionStatus[]>([])
  const displayedStatusRef = useRef<ConnectionStatus>('disconnected')
  const lastStatusChangeRef = useRef(0)

  const applyStableStatus = (candidate: ConnectionStatus) => {
    const window = statusWindowRef.current
    window.push(candidate)
    if (window.length > STABILITY_WINDOW) window.shift()

    const stable = resolveStableStatus(window, displayedStatusRef.current)
    if (!stable) return

    const elapsed = Date.now() - lastStatusChangeRef.current
    if (elapsed < MIN_HOLD_MS && stable !== 'disconnected') return

    displayedStatusRef.current = stable
    lastStatusChangeRef.current = Date.now()
    setConnectionStatus(stable)
    setNetworkMessage(toConnectionMessage(stable))
  }

  const resetStabilization = (status: ConnectionStatus) => {
    statusWindowRef.current = []
    displayedStatusRef.current = status
    lastStatusChangeRef.current = 0
    setConnectionStatus(status)
    setNetworkMessage(toConnectionMessage(status))
  }

  useEffect(() => {
    mountedRef.current = true
    engine.setListeners({
      onRemoteUsersChanged: (users) => {
        if (!mountedRef.current) return
        setRemoteUsers(users)
      },
      onConnectionStateChanged: (state) => {
        if (!mountedRef.current) return
        setConnectionState(state)
        if (state === 'DISCONNECTED') {
          resetStabilization('disconnected')
        }
      },
      onNetworkQuality: (quality) => {
        if (!mountedRef.current) return
        setNetworkQuality(quality)
        const candidate = toConnectionStatus(quality.overallQuality)
        applyStableStatus(candidate)
      },
      onTokenWillExpire: () => {
        if (!mountedRef.current) return
        setTokenWillExpire(true)
      },
      onTokenDidExpire: () => {
        if (!mountedRef.current) return
        setTokenDidExpire(true)
      },
      onError: (error) => {
        if (!mountedRef.current) return
        setJoinError(error.message)
      },
    })

    return () => {
      mountedRef.current = false
    }
  }, [engine])

  const joinRoom = useCallback(
    async (
      channel: string,
      token: string,
      uid: number,
      schedulingId?: string
    ) => {
      const request = ++joinRequestRef.current
      setIsJoining(true)
      setJoinError(null)
      setTokenWillExpire(false)
      setTokenDidExpire(false)
      try {
        return await engine.joinRoom(channel, token, uid, schedulingId)
      } catch (error) {
        if (mountedRef.current) setJoinError((error as Error).message)
        throw error
      } finally {
        // Sem setState após o unmount (warning do React 16 a cada encerramento).
        if (mountedRef.current && request === joinRequestRef.current) {
          setIsJoining(false)
        }
      }
    },
    [engine]
  )

  const leaveRoom = useCallback(async () => {
    const result = await engine.leaveRoom()
    if (!mountedRef.current) return result
    setRemoteUsers([])
    setConnectionState('DISCONNECTED')
    resetStabilization('disconnected')
    return result
  }, [engine])

  return {
    engine,
    remoteUsers,
    connectionState,
    networkQuality,
    connectionStatus,
    networkMessage,
    joinError,
    tokenWillExpire,
    tokenDidExpire,
    isJoining,
    joinRoom,
    leaveRoom,
  }
}
